import { ReactNode } from 'react';
import { TimelineIconProps } from '@/common/components/timeline/subcomponents/timeline-icon/TimelineIcon.tsx';
import { Timeline } from '@/common/components/timeline/Timeline';
import { red } from '@mui/material/colors';
import Stack from '@mui/material/Stack';
import { EventTime } from '@/features/events/components/events-timeline/subcomponents/event-time/EventTime.tsx';
import Typography from '@mui/material/Typography';
import { formatEventTimeDiff } from '@/features/events/components/events-timeline/utils/formatEventTimeDiff.ts';
import Button from '@mui/material/Button';
import { Trans } from '@lingui/react/macro';
import Box from '@mui/material/Box';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';

export interface EventTimelineGroupItemProps {
  timeStart: Date;
  timeStartUtcOffset: number;
  timeEnd: Date;
  timeEndUtcOffset: number;
  isLast?: boolean;
  prevEventAt?: Date;
  iconProps: TimelineIconProps;
  title: ReactNode;
  labels?: ReactNode;
  importantEvent?: boolean;
  childCount: number;
  errorCount: number;
  countLabel?: ReactNode;
  onShowChildEvents?: () => void;
}

export const EventTimelineGroupItem = ({
  timeStart,
  timeStartUtcOffset,
  timeEnd,
  timeEndUtcOffset,
  iconProps,
  childCount,
  errorCount,
  prevEventAt,
  labels,
  onShowChildEvents,
  isLast,
  title,
  importantEvent,
  countLabel,
}: EventTimelineGroupItemProps) => {
  const hasError = errorCount > 0;
  const allError = errorCount === childCount;

  return (
    <Timeline.Item
      isLast={isLast}
      icon={
        <Timeline.IconGroup
          icons={[
            <Timeline.Icon {...iconProps} key={1} color={allError ? red : iconProps.color} />,
            <Timeline.Icon {...iconProps} key={2} color={hasError ? red : iconProps.color} />,
            <Timeline.Icon {...iconProps} key={3} color={allError ? red : iconProps.color} />,
          ]}
        />
      }
      leftContent={
        <Stack>
          <EventTime eventAt={timeEnd} eventUtcOffset={timeEndUtcOffset} horizontalAlign="end" />
          <Typography textAlign="center" pl={6} color="textDisabled">
            ⋮
          </Typography>
          <EventTime
            eventAt={timeStart}
            eventUtcOffset={timeStartUtcOffset}
            horizontalAlign="end"
          />
        </Stack>
      }
      lineBadge={
        prevEventAt ? (
          <Timeline.LineBadge>{formatEventTimeDiff(timeStart, prevEventAt)}</Timeline.LineBadge>
        ) : undefined
      }
      rightContent={
        <Stack pt={{ xs: 0.5, md: 2 }}>
          <Stack pb={1} display={{ xs: 'block', md: 'none' }}>
            <EventTime eventAt={timeEnd} eventUtcOffset={timeEndUtcOffset} />
            <Typography textAlign="left" pl={10} color="textDisabled">
              ⋮
            </Typography>
            <EventTime eventAt={timeStart} eventUtcOffset={timeStartUtcOffset} />
          </Stack>
          <Stack direction="row" gap={2} alignItems="center">
            <Typography
              fontWeight={500}
              fontSize="1.2em"
              color={importantEvent ? 'textPrimary' : 'textSecondary'}
            >
              {title}
            </Typography>
            {labels}
          </Stack>
          <Typography variant="body2">{countLabel}</Typography>
          <Box mt={2}>
            <Button
              variant="outlined"
              size="small"
              onClick={onShowChildEvents}
              startIcon={<FormatListBulletedOutlinedIcon />}
            >
              <Trans>View events</Trans>
            </Button>
          </Box>
        </Stack>
      }
    />
  );
};
